<template>
  <div id="shipper-deliveries">
    <BreadCrumb :items="breadcrumbItems" />
    <v-card elevation="1" class="pb-5 px-5 mt-1" :loading="isLoading">
      <div class="d-flex justify-space-between align-center pt-5">
        <h1>Gerenciamento de entregas</h1>
      </div>
      <shipper-deliveries-filters
        :states="states"
        :cities="cities"
        :statuses="statuses"
        v-model="filters"
        @searchClick="onSearchClick"
        @stateChange="getCities"
        @exportClick="onExportClick"
        class="py-4"
      ></shipper-deliveries-filters>
      <v-tabs class="py-4" v-model="selectedTab">
        <v-tab>Hierarquia</v-tab>
        <v-tab>Gerenciamento Rápido</v-tab>
        <v-tab-item>
          <div class="table p-relative">
            <table-title-row :columnsOptions="deliveryColumnOptions"></table-title-row>
            <template v-for="(delivery, deliveryIndex) in deliveries">
              <div :key="delivery.data.id">
                <table-body-row
                  :node="delivery"
                  :nodeIndex="deliveryIndex"
                  :columns="deliveryColumns"
                  :lengthDataToTruncate="12"
                  :type="'Entrega'"
                  :enableCheckbox="true"
                  :enableDropdown="false"
                  @changeCheckbox="changeSelectedCheckbox"
                  @onExpand="onDeliveryExpand(delivery, deliveryIndex)"
                >
                </table-body-row>
                <Status :nodeIndex="deliveryIndex" :status="delivery.data.deliveryStatus" />

                <v-row v-if="delivery.open" class="my-1 mx-1">
                  <v-col cols="12">
                    <v-card :loading="isNfeLoading">
                      <v-card-text class="p-5">
                        <table-title-row :columnsOptions="nfeColumnOptions"></table-title-row>
                        <template v-for="(nfe, nfeIndex) in delivery.children">
                          <div :key="nfe.data.id">
                            <table-body-row
                              :node="nfe"
                              :nodeIndex="nfeIndex"
                              :columns="nfeColumns"
                              :lengthDataToTruncate="20"
                              :type="'Nota Fiscal'"
                              :enableCheckbox="true"
                              :enableDropdown="true"
                              :deliveryIndex="deliveryIndex"
                              :itemsDropdown="itemsDropdownNfe"
                              @changeCheckbox="changeSelectedCheckbox"
                              @actionsDropdown="actionsDropdown"
                            >
                            </table-body-row>

                            <Status :nodeIndex="nfeIndex" :status="nfe.data.nfeStatus" />
                          </div>
                        </template>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </template>
          </div>
        </v-tab-item>
        <v-tab-item>
          <div class="table p-relative">
            <table-title-row :columnsOptions="nfeFastManagementColumnOptions"></table-title-row>
            <template v-for="(nfe, nfeIndex) in nfesFastManagement">
              <div :key="nfe.data.id">
                <table-body-row
                  :node="nfe"
                  :nodeIndex="nfeIndex"
                  :columns="nfeFastManagementColumns"
                  :lengthDataToTruncate="20"
                  :type="'Nota Fiscal'"
                  :enableCheckbox="true"
                  :enableDropdown="true"
                  :itemsDropdown="itemsDropdownNfe"
                  @changeCheckbox="changeSelectedCheckboxFast"
                  @actionsDropdown="actionsDropdown"
                >
                </table-body-row>
                <Status :nodeIndex="nfeIndex" :status="nfe.data.nfeStatus" />
              </div>
            </template>
          </div>
        </v-tab-item>
      </v-tabs>

      <div class="d-flex align-center justify-space-between">
        <v-pagination
          v-if="isNfesTab"
          v-model="nfePageNumber"
          :length="nfeTotalPages"
          :total-visible="7"
          @input="onNfePageChange"
        ></v-pagination>
        <v-pagination
          v-else
          v-model="pageNumber"
          :length="totalPages"
          :total-visible="7"
          @input="onPageChange"
        ></v-pagination>
        <div class="d-flex align-center justify-end flex-wrap" style="gap: 12px">
          <v-btn
            @click="backupReceipts"
            :loading="isBackupLoading"
            :disabled="!selectedCheckbox.length"
            elevation="2"
            color="info"
            small
          >
            Baixar Canhotos
            <template #loader>
              <v-progress-circular indeterminate color="white" size="16" width="3"></v-progress-circular>
            </template>
          </v-btn>
          <v-btn :disabled="!selectedCheckbox.length" elevation="2" color="info" small @click="onPrintReceiptsClick()">
            Imprimir Canhotos
          </v-btn>
          <items-per-page-selector @change="onPageSizeChange" class="pl-5"></items-per-page-selector>
        </div>
      </div>
    </v-card>
    <nfe-receipt :open="isReceiptOpen" :nfeId="selectedNfeId" @closeNfeReceipt="onCloseReceipt" readonly />
    <historic-nfe :id="selectedNfeId" :open="isReceiptHistoryOpen" @closeHistoricNfe="onCloseReceiptHistory" />
    <MessagesError :type="errorsType" :errors="errors" @closeMessages="errors = []" />
    <ExportReportDialog
      :isOpen.sync="isExportReportOpen"
      :filters="filters"
      :states="states"
      :cities="cities"
      :status="statuses"
      @errorOnExport="onExportError"
      is-shipper
    />
    <export-confirm-dialog
      :isOpen.sync="isExportConfirmOpen"
      :isConfirmBtnLoading="isExportLoading"
      @confirmClick="onExportConfirm"
      title="Exportar"
      body-text="Você confirma a exportação?"
    />
  </div>
</template>

<script>
import BreadCrumb from '@/components/BreadCrumb.vue';
import ShipperDeliveriesFilters from '@/components/ShipperDeliveries/ShipperDeliveriesFilters.vue';
import TableTitleRow from '@/components/Table/Table-Title-Row.vue';
import TableBodyRow from '@/components/Table/Table-Body-Row.vue';
import Status from '@/components/Table/Status.vue';
import changeCheckbox from '@/utils/changeSelectedCheckbox';
import NfeReceipt from '@/components/Nfe/NfeReceipt.vue';
import HistoricNfe from '@/components/Nfe/HistoricNfe.vue';
import { mapActions, mapMutations } from 'vuex';
import { FeedbackMessageHandler } from '@/store/modules/feedbackMessageHandler';
import MessagesError from '@/components/MessagesError.vue';
import ExportReportDialog from '@/components/Shipment/ExportReportDialog';
import ItemsPerPageSelector from '@/components/ItemsPerPageSelector.vue';
import ExportConfirmDialog from '@/components/ConfirmDialog';
export default {
  components: {
    BreadCrumb,
    ShipperDeliveriesFilters,
    TableTitleRow,
    TableBodyRow,
    Status,
    NfeReceipt,
    HistoricNfe,
    MessagesError,
    ExportReportDialog,
    ItemsPerPageSelector,
    ExportConfirmDialog
  },
  data: () => ({
    statuses: [],
    states: [],
    cities: [],
    isExportLoading: false,
    isExportReportOpen: false,
    isExportConfirmOpen: false,
    isBackupLoading: false,
    selectedTab: 0,
    isReceiptOpen: false,
    isReceiptHistoryOpen: false,
    selectedNfe: null,
    selectedCheckbox: [],
    nfeTotalPages: 0,
    nfePageNumber: 1,
    nfePageSize: 10,
    totalPages: 0,
    pageNumber: 1,
    pageSize: 10,
    isNfeLoading: false,
    deliveries: [],
    nfesFastManagement: [],
    deliveryColumns: [
      'type',
      'boardingNumber',
      'numberCte',
      'costFreight',
      'totalNfes',
      'cityRecipient',
      'authorizationDate',
      'deliveryForecast',
      'emissionDate',
      'nfeProgress',
      'actions'
    ],
    nfeColumns: ['type', 'number', 'price', 'weight', 'completionDate', 'receiptStatus', 'actions'],
    nfeFastManagementColumns: [
      'type',
      'boardingNumber',
      'numberCte',
      'number',
      'price',
      'weight',
      'cityRecipient',
      'completionDate',
      'receiptStatus',
      'actions'
    ],
    nfeFastManagementColumnOptions: [
      {
        value: 'type',
        text: 'Tipo'
      },
      {
        value: 'boardingNumber',
        text: 'N° Embarque Externo'
      },
      {
        value: 'numberCte',
        text: 'Número CTE'
      },
      {
        value: 'number',
        text: 'Número NFE'
      },
      {
        value: 'price',
        text: 'Preço da NF'
      },
      {
        value: 'weight',
        text: 'Peso da NF'
      },
      {
        value: 'cityRecipient',
        text: 'Cidade do Destinatário'
      },
      {
        value: 'completionDate',
        text: 'Data de Conclusão'
      },
      {
        value: 'receiptStatus',
        text: 'Backup do canhoto'
      },
      {
        value: 'actions',
        text: 'Ações'
      }
    ],
    deliveryColumnOptions: [
      {
        value: 'type',
        text: 'Tipo'
      },
      {
        value: 'boardingNumber',
        text: 'N° Embarque Externo'
      },

      {
        value: 'numberCte',
        text: 'Número CTE'
      },
      {
        value: 'costFreight',
        text: 'Custo do Frete'
      },
      {
        value: 'totalNfes',
        text: 'Preço Total de NFE'
      },
      {
        value: 'cityRecipient',
        text: 'Cidade do Destinatário'
      },
      {
        value: 'authorizationDate',
        text: 'Data de Autorização'
      },
      {
        value: 'deliveryForecast',
        text: 'Previsão de Entrega'
      },
      {
        value: 'emissionDate',
        text: 'Data de Emissão'
      },
      {
        value: 'nfeProgress',
        text: 'Conclusão'
      },
      {
        value: 'actions',
        text: 'Ações'
      }
    ],
    nfeColumnOptions: [
      {
        value: 'type',
        text: 'Tipo'
      },
      {
        value: 'number',
        text: 'Número NFE'
      },
      {
        value: 'price',
        text: 'Preço da NF'
      },
      {
        value: 'weight',
        text: 'Peso da NF'
      },
      {
        value: 'completionDate',
        text: 'Data de Conclusão'
      },
      {
        value: 'receiptStatus',
        text: 'Backup do canhoto'
      },
      {
        value: 'actions',
        text: 'Ações'
      }
    ],
    isLoading: false,
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Entregas',
        disabled: false,
        to: '/shipper-deliveries'
      }
    ],

    filters: {
      initialForecastDate: null,
      endForecastDate: null,
      initialCompletionDate: null,
      endCompletionDate: null,
      isReceiptDownloaded: null,
      isReceiptPurged: null,
      recipient: null,
      initialAuthDate: null,
      endAuthDate: null,
      stateId: null,
      cityId: null,
      hasReceiptFilter: null,
      isReceiptValidFilter: null,
      status: [],
      boardingNumber: [],
      keyNfe: [],
      numberCTE: [],
      numberInvoiceId: null
    },
    itemsDropdownNfe: [{ title: 'Canhoto da Nota' }, { title: 'Histórico' }],
    errors: [],
    errorsType: 'error'
  }),

  computed: {
    selectedNfeId() {
      return this.selectedNfe ? this.selectedNfe.data.id : null;
    },
    isNfesTab() {
      return this.selectedTab === 1;
    },
    getTheLastThirtyDays() {
      const now = new Date();
      const currentDay = now;
      const thirtyDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);

      return {
        thirtyDayAgo: thirtyDayAgo.toISOString().slice(0, 10),
        currentDay: currentDay.toISOString().slice(0, 10)
      };
    }
  },

  async created() {
    this.filterTheLastThirtyDays();
    this.getStates();
    this.fetchAllStatus();
    this.getAllDeliveries();
    this.getAllNfesFastManagement();
  },

  methods: {
    ...mapActions('ReportStore', ['report']),
    async onExportConfirm() {
      try {
        this.isExportLoading = true;
        const payload = {
          isShipper: true,
          filters: this.filters,
          body: {
            initialDate: this.filters.initialAuthDate,
            dateEnd: this.filters.endAuthDate,
            holidays: []
          }
        };
        const response = await this.report(payload);
        if (response.success) {
          FeedbackMessageHandler.setFeedbackMessage(this, 'Dados reportados com sucesso!');
          this.$router.push('shipper-deliveries-export');
        } else {
          this.errors = response.errors;
          this.errorsType = 'error';
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isExportLoading = false;
      }
    },
    ...mapActions('AdressStore', ['fetchStates', 'fetchCities']),
    ...mapActions('NfeStore', ['fetchStatus']),
    ...mapActions('ShippersStore', [
      'getShipperDeliveries',
      'getShipperNfes',
      'getShipperNfesFastManagement',
      'uploadReceiptToBackup'
    ]),
    ...mapMutations('NfeStore', ['setSelectedCheckbox']),
    ...mapMutations('ShipmentStore', [
      'setFiltersManegementDeliveryForecastDateStart',
      'setFiltersManegementDeliveryForecastDateEnd',
      'setFiltersManegementCompletionDateStart',
      'setFiltersManegementCompletionDateEnd',
      'setFiltersManegementAuthorizationDateStart',
      'setFiltersManegementAuthorizationDateEnd',
      'setFiltersManegementStateId',
      'setFiltersManegementCityId',
      'setFiltersManegementHasReceipt',
      'setFiltersManegementIsReceiptValid',
      'setFiltersManegementTranshipmentId',
      'setFiltersManegementIsTransferred',
      'setFiltersManegementCteNumber',
      'setFiltersManegementSenderName',
      'setFiltersManegementRecipientName',
      'setFiltersManegementNfeNumber',
      'setFiltersManegementBoardingNumber',
      'setFiltersManegementDirectDelivererId',
      'setFiltersManegementBoardingId',
      'setFiltersManegementStatus',
      'setFiltersManegementNumberInvoiceId'
    ]),

    filterTheLastThirtyDays() {
      const { thirtyDayAgo, currentDay } = this.getTheLastThirtyDays;
      this.filters.initialAuthDate = thirtyDayAgo;
      this.filters.endAuthDate = currentDay;
    },

    async getAllDeliveries() {
      try {
        this.selectedCheckbox = [];
        this.isLoading = true;
        const { data, totalPages } = await this.getShipperDeliveries({
          ...this.filters,
          pageSize: this.pageSize,
          pageNumber: this.pageNumber
        });
        this.deliveries = data;
        this.totalPages = totalPages;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    async getAllNfesFastManagement() {
      try {
        this.isLoading = true;
        const { data, totalPages } = await this.getShipperNfesFastManagement({
          ...this.filters,
          pageSize: this.pageSize,
          pageNumber: this.nfePageNumber
        });
        this.nfesFastManagement = data;
        this.nfeTotalPages = totalPages;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async onDeliveryExpand(delivery, deliveryIndex) {
      this.isNfeLoading = true;
      const { open: isOpen, selected } = delivery;
      this.deliveries[deliveryIndex].open = !isOpen;

      if (!isOpen) {
        try {
          this.deliveries[deliveryIndex].children = await this.getShipperNfes({ deliveryId: delivery.data.id });
          this.deliveries[deliveryIndex].children.forEach((item) => {
            if (selected) {
              const selectedIds = this.selectedCheckbox.map((item) => item.id);
              if (!selectedIds.includes(item.data.id)) {
                this.selectedCheckbox.push(item.data);
              }
              item.selected = true;
            }
          });
        } catch (error) {
          console.error(error);
        } finally {
          this.isNfeLoading = false;
        }
      }
    },

    async backupReceipts() {
      try {
        this.isBackupLoading = true;
        const body = {
          filters: this.filters,
          selectedItems: this.selectedCheckbox
        };
        body.filters.keyNfe = body.filters.keyNfe.map((item) => Number(item.trim()));
        body.filters.numberCTE = body.filters.numberCTE.map((item) => Number(item.trim()));
        body.filters.boardingNumber = body.filters.boardingNumber.map((item) => Number(item.trim()));
        const response = await this.uploadReceiptToBackup(body);
        if (response.success) {
          FeedbackMessageHandler.setFeedbackMessage(this, 'Backup criado com sucesso!');
          this.$router.push('receipts-backup');
        } else {
          this.errors = response.errors;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isBackupLoading = false;
      }
    },
    async getStates() {
      try {
        this.states = await this.fetchStates();
      } catch (error) {
        console.error(error);
      }
    },

    async getCities(stateId) {
      try {
        this.cities = await this.fetchCities(stateId);
      } catch (error) {
        console.error(error);
      }
    },

    async fetchAllStatus() {
      try {
        this.statuses = await this.fetchStatus();
      } catch (err) {
        console.error(err);
      }
    },

    onExportError({ errorsType, errors }) {
      this.errors = errors;
      this.errorsType = errorsType;
    },

    onPageSizeChange(value) {
      this.pageSize = value;
      this.pageNumber = 1;
      this.nfePageNumber = 1;
      this.getAllDeliveries();
      this.getAllNfesFastManagement();
    },

    onNfePageChange(pageNumber) {
      this.nfePageNumber = pageNumber;
      this.getAllNfesFastManagement();
    },

    onPageChange(pageNumber) {
      this.pageNumber = pageNumber;
      this.getAllDeliveries();
    },

    onSearchClick() {
      this.resetPages();
      this.getAllDeliveries();
      this.getAllNfesFastManagement();
    },

    changeSelectedCheckbox(item, children, _, cargoIndex, stopIndex, deliveriesIndex) {
      this.selectedCheckbox = changeCheckbox(
        this.selectedCheckbox,
        item,
        children,
        cargoIndex,
        stopIndex,
        deliveriesIndex,
        this.deliveries[deliveriesIndex]
      );
    },

    changeSelectedCheckboxFast(item) {
      this.selectedCheckbox = changeCheckbox(this.selectedCheckbox, item);
    },

    actionsDropdown(index, item) {
      this.selectedNfe = item;
      if (!index) {
        this.isReceiptOpen = true;
      } else if (index === 1) {
        this.isReceiptHistoryOpen = true;
      }
    },

    onPrintReceiptsClick() {
      const {
        recipient,
        initialForecastDate,
        endForecastDate,
        initialCompletionDate,
        endCompletionDate,
        initialAuthDate,
        endAuthDate,
        stateId,
        cityId,
        hasReceiptFilter,
        isReceiptValidFilter,
        numberCTE,
        keyNfe,
        boardingNumber,
        status,
        numberInvoiceId
      } = this.filters;
      this.setFiltersManegementDeliveryForecastDateStart(initialForecastDate);
      this.setFiltersManegementDeliveryForecastDateEnd(endForecastDate);
      this.setFiltersManegementCompletionDateStart(initialCompletionDate);
      this.setFiltersManegementCompletionDateEnd(endCompletionDate);
      this.setFiltersManegementAuthorizationDateStart(initialAuthDate);
      this.setFiltersManegementAuthorizationDateEnd(endAuthDate);
      this.setFiltersManegementStateId(stateId);
      this.setFiltersManegementCityId(cityId);
      this.setFiltersManegementHasReceipt(hasReceiptFilter);
      this.setFiltersManegementIsReceiptValid(isReceiptValidFilter);
      this.setFiltersManegementCteNumber(numberCTE);
      this.setFiltersManegementNfeNumber(keyNfe);
      this.setFiltersManegementBoardingNumber(boardingNumber);
      this.setFiltersManegementStatus(status);
      this.setFiltersManegementRecipientName(recipient);
      this.setFiltersManegementNumberInvoiceId(numberInvoiceId);

      this.setSelectedCheckbox(this.selectedCheckbox);
      this.$router.push('/shipper-deliveries/receipts');
    },

    onExportClick() {
      this.isExportConfirmOpen = true;
    },

    onCloseReceipt() {
      this.isReceiptOpen = false;
    },

    onCloseReceiptHistory() {
      this.isReceiptHistoryOpen = false;
    },

    resetPages() {
      this.pageNumber = 1;
      this.nfePageNumber = 1;
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep .v-pagination.v-pagination {
  justify-content: start;
  position: inherit;
  width: auto;
}

::v-deep .col-md.text-actions {
  max-width: 50px !important;
}

::v-deep .title-table.text-actions {
  max-width: 60px !important;
}
</style>
